import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DateField from '../components/DateField';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SubscriptionTab from '../components/SubscriptionTab';
import { 
  Calendar, User, Lock, Building2, Sun, 
  Mail, Download, Plus, Trash2, Edit2, Moon,
  Save, AlertCircle, X, DatabaseZap, ChevronLeft, ChevronRight, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert text to uppercase without accents
const toUpperCaseNoAccents = (str) => {
  if (!str) return '';
  const accentsMap = {
    'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
    'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
    'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
  };
  return str.toUpperCase().split('').map(char => accentsMap[char] || char).join('');
};

// Court types by procedure category
const CIVIL_COURT_TYPES = [
  { value: 'single_first', label: 'Μονομελές Πρωτοδικείο' },
  { value: 'multi_first', label: 'Πολυμελές Πρωτοδικείο' },
  { value: 'single_appeal', label: 'Μονομελές Εφετείο' },
  { value: 'triple_appeal', label: 'Τριμελές Εφετείο' },
  { value: 'supreme', label: 'Άρειος Πάγος' },
];

const CRIMINAL_COURT_TYPES = [
  { value: 'single_criminal', label: 'Μονομελές Πλημμελειοδικείο' },
  { value: 'three_criminal', label: 'Τριμελές Πλημμελειοδικείο' },
  { value: 'single_criminal_appeal', label: 'Μονομελές Εφετείο Κακουργημάτων' },
  { value: 'triple_criminal_appeal', label: 'Τριμελές Εφετείο Κακουργημάτων' },
  { value: 'five_criminal_appeal', label: 'Πενταμελές Εφετείο' },
  { value: 'mixed_court', label: 'Μικτό Ορκωτό Δικαστήριο' },
  { value: 'mixed_jury', label: 'Μικτό Ορκωτό' },
  { value: 'appeal_criminal', label: 'Εφετείο Κακουργημάτων' },
  { value: 'supreme_criminal', label: 'Άρειος Πάγος (Ποινικό)' },
];

const ADMIN_COURT_TYPES = [
  { value: 'single_admin_first', label: 'Μονομελές Διοικητικό Πρωτοδικείο' },
  { value: 'triple_admin_first', label: 'Τριμελές Διοικητικό Πρωτοδικείο' },
  { value: 'admin_appeal', label: 'Διοικητικό Εφετείο' },
  { value: 'council_of_state', label: 'Συμβούλιο της Επικρατείας' },
];

const Settings = ({ onDataChanged, initialTab = 'profile' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [mobileShowContent, setMobileShowContent] = useState(initialTab !== 'profile');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Sync activeTab when initialTab prop changes (e.g., user clicks credits badge → re-opens with subscription tab)
  React.useEffect(() => {
    setActiveTab(initialTab);
    setMobileShowContent(initialTab !== 'profile');
  }, [initialTab]);
  
  // PIN input refs
  const currentPinRef = React.useRef(null);
  const newPinRef = React.useRef(null);
  const confirmPinRef = React.useRef(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    profile_name: '',
    profile_surname: '',
    profile_email: '',
    profile_phone: '',
  });
  
  // PIN state
  const [pinForm, setPinForm] = useState({
    current_pin: '',
    new_pin: '',
    confirm_pin: '',
  });
  
  // Email notification state
  const [emailForm, setEmailForm] = useState({
    email_notifications_enabled: false,
    email_address: '',
    email_reminder_days: [1, 3, 7],
  });
  
  // Appearance state
  const [appearanceForm, setAppearanceForm] = useState({
    theme: 'light',
    font_size: 'medium',
  });
  
  // Courts state
  const [courts, setCourts] = useState([]);
  const [showCourtForm, setShowCourtForm] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [courtForm, setCourtForm] = useState({
    name: '',
    court_type: '',
    procedure_category: 'civil', // 'civil', 'criminal', 'administrative'
    location: '',
    is_favorite: true, // Default to favorite since user is adding it
  });
  
  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    date: '',
    location: '',
    notes: '',
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [settingsRes, courtsRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/settings/courts`),
        axios.get(`${API}/settings/holidays`),
      ]);
      
      const s = settingsRes.data;
      setSettings(s);
      
      // Initialize forms with data
      setProfileForm({
        profile_name: s.profile_name || '',
        profile_surname: s.profile_surname || '',
        profile_email: s.profile_email || '',
        profile_phone: s.profile_phone || '',
      });
      
      setEmailForm({
        email_notifications_enabled: s.email_notifications_enabled || false,
        email_address: s.email_address || '',
        email_reminder_days: s.email_reminder_days || [1, 3, 7],
      });
      
      setAppearanceForm({
        theme: s.theme || 'light',
        font_size: s.font_size || 'medium',
      });
      
      setCourts(courtsRes.data);
      setHolidays(holidaysRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Σφάλμα φόρτωσης ρυθμίσεων');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings`, profileForm);
      toast.success('Τα στοιχεία αποθηκεύτηκαν');
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const changePin = async () => {
    if (pinForm.new_pin.length !== 4 || !/^\d{4}$/.test(pinForm.new_pin)) {
      toast.error('Το PIN πρέπει να είναι 4 ψηφία');
      return;
    }
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      toast.error('Τα PIN δεν ταιριάζουν');
      return;
    }
    
    try {
      const payload = { new_pin: pinForm.new_pin };
      if (settings.financial_pin) {
        payload.current_pin = pinForm.current_pin;
      }
      
      await axios.post(`${API}/financial/set-pin`, payload);
      toast.success('Το PIN άλλαξε επιτυχώς');
      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αλλαγής PIN');
    }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings`, emailForm);
      toast.success('Οι ρυθμίσεις email αποθηκεύτηκαν');
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const saveAppearance = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings`, appearanceForm);
      toast.success('Οι ρυθμίσεις εμφάνισης αποθηκεύτηκαν');
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const toggleReminderDay = (day) => {
    const days = emailForm.email_reminder_days;
    if (days.includes(day)) {
      setEmailForm({ ...emailForm, email_reminder_days: days.filter(d => d !== day) });
    } else {
      setEmailForm({ ...emailForm, email_reminder_days: [...days, day].sort((a, b) => a - b) });
    }
  };

  // Courts CRUD
  const saveCourt = async () => {
    if (!courtForm.court_type || !courtForm.location) {
      toast.error('Συμπληρώστε δικαστήριο και έδρα');
      return;
    }
    
    try {
      if (editingCourt) {
        await axios.put(`${API}/settings/courts/${editingCourt.id}`, courtForm);
        toast.success('Το δικαστήριο ενημερώθηκε');
      } else {
        await axios.post(`${API}/settings/courts`, courtForm);
        toast.success('Το δικαστήριο προστέθηκε');
      }
      resetCourtForm();
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const deleteCourt = async (id) => {
    if (!window.confirm('Διαγραφή δικαστηρίου;')) return;
    try {
      await axios.delete(`${API}/settings/courts/${id}`);
      toast.success('Το δικαστήριο διαγράφηκε');
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const resetCourtForm = () => {
    setEditingCourt(null);
    setCourtForm({
      name: '',
      court_type: '',
      procedure_category: 'civil',
      location: '',
      is_favorite: true,
    });
    setShowCourtForm(false);
  };

  // Get court types based on procedure category
  const getCourtTypesForCategory = (category) => {
    switch (category) {
      case 'civil': return CIVIL_COURT_TYPES;
      case 'criminal': return CRIMINAL_COURT_TYPES;
      case 'administrative': return ADMIN_COURT_TYPES;
      default: return CIVIL_COURT_TYPES;
    }
  };

  // Filter courts by category
  const civilCourts = courts.filter(c => c.procedure_category === 'civil');
  const criminalCourts = courts.filter(c => c.procedure_category === 'criminal');
  const adminCourts = courts.filter(c => c.procedure_category === 'administrative');

  // Holidays CRUD
  const saveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date) {
      toast.error('Συμπληρώστε όνομα και ημερομηνία');
      return;
    }
    
    try {
      if (editingHoliday) {
        await axios.put(`${API}/settings/holidays/${editingHoliday.id}`, holidayForm);
        toast.success('Η αργία ενημερώθηκε');
      } else {
        await axios.post(`${API}/settings/holidays`, holidayForm);
        toast.success('Η αργία προστέθηκε');
      }
      resetHolidayForm();
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm('Διαγραφή αργίας;')) return;
    try {
      await axios.delete(`${API}/settings/holidays/${id}`);
      toast.success('Η αργία διαγράφηκε');
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const startEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setHolidayForm({
      name: holiday.name,
      date: holiday.date,
      location: holiday.location || '',
      notes: holiday.notes || '',
    });
    setShowHolidayForm(true);
  };

  const resetHolidayForm = () => {
    setEditingHoliday(null);
    setHolidayForm({
      name: '',
      date: '',
      location: '',
      notes: '',
    });
    setShowHolidayForm(false);
  };

  // Export functions
  const exportEntries = () => {
    window.open(`${API}/settings/export/entries`, '_blank');
    toast.success('Έναρξη λήψης...');
  };

  const exportFinancial = () => {
    window.open(`${API}/settings/export/financial`, '_blank');
    toast.success('Έναρξη λήψης...');
  };

  // Google Calendar
  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/calendar`, { withCredentials: true });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Σφάλμα σύνδεσης με Google Calendar');
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await axios.post(`${API}/auth/google/disconnect`, {}, { withCredentials: true });
      toast.success('Η σύνδεση αποσυνδέθηκε');
      fetchAllData();
    } catch (error) {
      toast.error('Σφάλμα αποσύνδεσης');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: 'Προφίλ', icon: User },
    { id: 'pin', label: 'PIN', icon: Lock },
    { id: 'subscription', label: 'Συνδρομή', icon: CreditCard },
    { id: 'calendar', label: 'Ημερολόγιο', icon: Calendar },
    { id: 'email', label: 'Ειδοποιήσεις', icon: Mail },
    { id: 'courts', label: 'Δικαστήρια', icon: Building2 },
    { id: 'holidays', label: 'Αργίες', icon: Sun },
    { id: 'appearance', label: 'Εμφάνιση', icon: Moon },
    { id: 'export', label: 'Εξαγωγή', icon: Download },
    { id: 'cleanup', label: 'Εκκαθάριση', icon: DatabaseZap },
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setMobileShowContent(true);
  };

  return (
    <div className="bg-slate-50 -m-6 p-4 sm:p-6">
      {/* Desktop: side-by-side */}
      <div className="hidden sm:flex gap-6">
        {/* Sidebar */}
        <div className="w-52 flex-shrink-0 bg-white rounded-lg border border-slate-200 shadow-sm p-4 h-fit">
          <nav className="space-y-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-red-700 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Στοιχεία χρήστη</h3>
              <p className="text-sm text-slate-500">Τα προσωπικά σας στοιχεία</p>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <Label className="text-sm text-slate-600">Όνομα</Label>
                  <Input
                    value={profileForm.profile_name}
                    onChange={(e) => setProfileForm({ ...profileForm, profile_name: toUpperCaseNoAccents(e.target.value) })}
                    placeholder="π.χ. ΙΩΑΝΝΗΣ"
                    className="mt-2 h-9 bg-white border-slate-300 uppercase text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Επώνυμο</Label>
                  <Input
                    value={profileForm.profile_surname}
                    onChange={(e) => setProfileForm({ ...profileForm, profile_surname: toUpperCaseNoAccents(e.target.value) })}
                    placeholder="π.χ. ΠΑΠΑΔΟΠΟΥΛΟΣ"
                    className="mt-2 h-9 bg-white border-slate-300 uppercase text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Email</Label>
                  <Input
                    type="email"
                    value={profileForm.profile_email}
                    onChange={(e) => setProfileForm({ ...profileForm, profile_email: e.target.value })}
                    placeholder="email@example.com"
                    className="mt-2 h-9 bg-white border-slate-300 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Τηλέφωνο</Label>
                  <Input
                    value={profileForm.profile_phone}
                    onChange={(e) => setProfileForm({ ...profileForm, profile_phone: e.target.value })}
                    placeholder="69x xxx xxxx"
                    className="mt-2 h-9 bg-white border-slate-300 text-sm"
                  />
                </div>
              </div>
            
              <button onClick={saveProfile} disabled={saving} className="mt-4 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all disabled:bg-slate-300 flex items-center gap-2">
                <Save className="w-4 h-4" />
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && <SubscriptionTab />}

        {/* PIN Tab */}
        {activeTab === 'pin' && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Διαχείριση PIN</h3>
              <p className="text-sm text-slate-500">
                {settings?.financial_pin 
                  ? 'Αλλαγή PIN για την Οικονομική διαχείριση' 
                  : 'Ορισμός PIN για την Οικονομική διαχείριση'}
              </p>
            </div>
            <div className="p-6">
              <div className="max-w-md mx-auto space-y-6">
                {settings?.financial_pin && (
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600 block text-center">Τρέχον PIN</Label>
                    <div 
                      className="flex justify-center gap-3 cursor-pointer"
                      onClick={() => currentPinRef.current?.focus()}
                    >
                      {[0, 1, 2, 3].map((index) => (
                        <div
                          key={index}
                          className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all ${
                            pinForm.current_pin.length > index 
                              ? 'border-red-500 bg-red-50 text-red-700' 
                              : 'border-slate-300 bg-white text-slate-400'
                          }`}
                        >
                          {pinForm.current_pin.length > index ? '●' : ''}
                        </div>
                      ))}
                    </div>
                    <input
                      ref={currentPinRef}
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={pinForm.current_pin}
                      onChange={(e) => setPinForm({ ...pinForm, current_pin: e.target.value.replace(/\D/g, '') })}
                      className="sr-only"
                      autoFocus
                      autoComplete="new-password"
                      data-lpignore="true"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 block text-center">Νέο PIN</Label>
                  <div 
                    className="flex justify-center gap-3 cursor-pointer"
                    onClick={() => newPinRef.current?.focus()}
                  >
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all ${
                          pinForm.new_pin.length > index 
                            ? 'border-red-500 bg-red-50 text-red-700' 
                            : 'border-slate-300 bg-white text-slate-400'
                        }`}
                      >
                        {pinForm.new_pin.length > index ? '●' : ''}
                      </div>
                    ))}
                  </div>
                  <input
                    ref={newPinRef}
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinForm.new_pin}
                    onChange={(e) => setPinForm({ ...pinForm, new_pin: e.target.value.replace(/\D/g, '') })}
                    className="sr-only"
                    autoComplete="new-password"
                    data-lpignore="true"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 block text-center">Επιβεβαίωση PIN</Label>
                  <div 
                    className="flex justify-center gap-3 cursor-pointer"
                    onClick={() => confirmPinRef.current?.focus()}
                  >
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold transition-all ${
                          pinForm.confirm_pin.length > index 
                            ? 'border-red-500 bg-red-50 text-red-700' 
                            : 'border-slate-300 bg-white text-slate-400'
                        }`}
                      >
                        {pinForm.confirm_pin.length > index ? '●' : ''}
                      </div>
                    ))}
                  </div>
                  <input
                    ref={confirmPinRef}
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinForm.confirm_pin}
                    onChange={(e) => setPinForm({ ...pinForm, confirm_pin: e.target.value.replace(/\D/g, '') })}
                    className="sr-only"
                    autoComplete="new-password"
                    data-lpignore="true"
                  />
                </div>
                
                <div className="flex justify-center pt-2">
                  <button 
                    onClick={changePin} 
                    className="px-6 py-2.5 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-lg transition-all flex items-center gap-2 disabled:bg-slate-300"
                    disabled={pinForm.new_pin.length !== 4 || pinForm.confirm_pin.length !== 4}
                  >
                    <Lock className="w-4 h-4" />
                    {settings?.financial_pin ? 'Αλλαγή PIN' : 'Ορισμός PIN'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Συγχρονισμός ημερολογίου</h3>
              <p className="text-sm text-slate-500">Σύνδεση με Google Calendar</p>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Google Calendar</p>
                    <p className="text-sm text-slate-500">
                      {settings?.google_calendar_enabled && settings?.google_access_token
                        ? '✓ Συνδεδεμένο'
                        : 'Δεν έχει συνδεθεί'}
                    </p>
                  </div>
                </div>
                
                {settings?.google_calendar_enabled && settings?.google_access_token ? (
                  <button onClick={disconnectGoogleCalendar} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-all">
                    Αποσύνδεση
                  </button>
                ) : (
                  <button onClick={connectGoogleCalendar} className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all">
                    Σύνδεση
                  </button>
                )}
              </div>
              
              <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-sm text-red-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Όταν συνδεθείτε, οι νέες εγγραφές θα προστίθενται αυτόματα στο Google Calendar σας.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Ειδοποιήσεις Email</h3>
              <p className="text-sm text-slate-500">Ρυθμίσεις υπενθυμίσεων</p>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="email_enabled"
                  checked={emailForm.email_notifications_enabled}
                  onChange={(e) => setEmailForm({ ...emailForm, email_notifications_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-red-700 focus:ring-red-700 accent-red-700"
                />
                <Label htmlFor="email_enabled" className="cursor-pointer text-sm text-slate-600">
                  Ενεργοποίηση ειδοποιήσεων email
                </Label>
              </div>
              
              {emailForm.email_notifications_enabled && (
                <>
                  <div className="max-w-sm">
                    <Label className="text-sm text-slate-600">Email για ειδοποιήσεις</Label>
                    <Input
                      type="email"
                      value={emailForm.email_address}
                      onChange={(e) => setEmailForm({ ...emailForm, email_address: e.target.value })}
                      placeholder="email@example.com"
                      className="mt-2 h-9 bg-white border-slate-300 text-sm"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600 mb-2 block">Υπενθύμιση πριν από (ημέρες)</Label>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 5, 7, 14, 30].map(day => (
                        <button
                          key={day}
                          onClick={() => toggleReminderDay(day)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            emailForm.email_reminder_days.includes(day)
                              ? 'bg-red-700 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {day} {day === 1 ? 'ημέρα' : 'ημέρες'}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            
              <button onClick={saveEmailSettings} disabled={saving} className="mt-4 px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all disabled:bg-slate-300 flex items-center gap-2">
                <Save className="w-4 h-4" />
                Αποθήκευση
              </button>
            </div>
          </div>
        )}

        {/* Courts Tab */}
        {activeTab === 'courts' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="font-semibold text-slate-900">Συνήθη Δικαστήρια</h3>
                  <p className="text-sm text-slate-500">Εμφανίζονται πρώτα στις λίστες</p>
                </div>
                <button onClick={() => setShowCourtForm(true)} className="self-start px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Προσθήκη
                </button>
              </div>
            </div>
            
            {showCourtForm && (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 space-y-4">
                <h4 className="font-semibold text-slate-900">{editingCourt ? 'Επεξεργασία' : 'Νέο Δικαστήριο'}</h4>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm text-slate-600">Κατηγορία *</Label>
                    <Select
                      value={courtForm.procedure_category}
                      onValueChange={(v) => setCourtForm({ ...courtForm, procedure_category: v, court_type: '' })}
                    >
                      <SelectTrigger data-testid="court-category-select" className="w-full mt-2 border-slate-300 rounded-md text-sm bg-white focus:ring-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="civil" className="text-sm">Αστικά</SelectItem>
                        <SelectItem value="criminal" className="text-sm">Ποινικά</SelectItem>
                        <SelectItem value="administrative" className="text-sm">Διοικητικά</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Δικαστήριο *</Label>
                    <Select
                      value={courtForm.court_type || undefined}
                      onValueChange={(v) => setCourtForm({ ...courtForm, court_type: v })}
                    >
                      <SelectTrigger data-testid="court-type-select" className="w-full mt-2 border-slate-300 rounded-md text-sm bg-white focus:ring-0">
                        <SelectValue placeholder="Επιλέξτε..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getCourtTypesForCategory(courtForm.procedure_category).map(ct => (
                          <SelectItem key={ct.value} value={ct.value} className="text-sm">{ct.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Έδρα *</Label>
                  <Input
                    value={courtForm.location}
                    onChange={(e) => setCourtForm({ ...courtForm, location: e.target.value })}
                    placeholder="π.χ. Αθήνα"
                    className="mt-2 h-9 bg-white border-slate-300 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={resetCourtForm} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-100 transition-all">Ακύρωση</button>
                  <button onClick={saveCourt} className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all">Αποθήκευση</button>
                </div>
              </div>
            )}
            
            {/* Αστικά Δικαστήρια */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-3 border-b border-slate-100 bg-slate-50">
                <h4 className="font-medium text-slate-700">Αστικά Δικαστήρια</h4>
              </div>
              {civilCourts.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">Δεν έχετε προσθέσει αστικά δικαστήρια</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {civilCourts.map(court => (
                    <div key={court.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-all">
                      <div>
                        <p className="font-medium text-slate-900">
                          {CIVIL_COURT_TYPES.find(c => c.value === court.court_type)?.label || court.court_type}
                        </p>
                        <p className="text-xs text-slate-500">{court.location}</p>
                      </div>
                      <button onClick={() => deleteCourt(court.id)} className="p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Ποινικά Δικαστήρια */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-3 border-b border-slate-100 bg-slate-50">
                <h4 className="font-medium text-slate-700">Ποινικά Δικαστήρια</h4>
              </div>
              {criminalCourts.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">Δεν έχετε προσθέσει ποινικά δικαστήρια</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {criminalCourts.map(court => (
                    <div key={court.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-all">
                      <div>
                        <p className="font-medium text-slate-900">
                          {CRIMINAL_COURT_TYPES.find(c => c.value === court.court_type)?.label || court.court_type}
                        </p>
                        <p className="text-xs text-slate-500">{court.location}</p>
                      </div>
                      <button onClick={() => deleteCourt(court.id)} className="p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Διοικητικά Δικαστήρια */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-3 border-b border-slate-100 bg-slate-50">
                <h4 className="font-medium text-slate-700">Διοικητικά Δικαστήρια</h4>
              </div>
              {adminCourts.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">Δεν έχετε προσθέσει διοικητικά δικαστήρια</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {adminCourts.map(court => (
                    <div key={court.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-all">
                      <div>
                        <p className="font-medium text-slate-900">
                          {ADMIN_COURT_TYPES.find(c => c.value === court.court_type)?.label || court.court_type}
                        </p>
                        <p className="text-xs text-slate-500">{court.location}</p>
                      </div>
                      <button onClick={() => deleteCourt(court.id)} className="p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Holidays Tab */}
        {activeTab === 'holidays' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="font-semibold text-slate-900">Τοπικές αργίες</h3>
                  <p className="text-sm text-slate-500">Προσθέστε ειδικές αργίες (π.χ. πολιούχοι)</p>
                </div>
                <button onClick={() => setShowHolidayForm(true)} className="self-start px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Προσθήκη
                </button>
              </div>
            </div>
            
            {showHolidayForm && (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 space-y-4">
                <h4 className="font-semibold text-slate-900">{editingHoliday ? 'Επεξεργασία' : 'Νέα αργία'}</h4>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm text-slate-600">Όνομα *</Label>
                    <Input
                      value={holidayForm.name}
                      onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                      placeholder="π.χ. Άγιος Διονύσιος"
                      className="mt-2 h-9 bg-white border-slate-300 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Ημερομηνία *</Label>
                    <div className="mt-2">
                      <DateField
                        testId="holiday-date-input"
                        value={holidayForm.date}
                        onChange={(v) => setHolidayForm({ ...holidayForm, date: v })}
                        className="w-full justify-start"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm text-slate-600">Τοποθεσία</Label>
                    <Input
                      value={holidayForm.location}
                      onChange={(e) => setHolidayForm({ ...holidayForm, location: e.target.value })}
                      placeholder="π.χ. Ζάκυνθος"
                      className="mt-2 h-9 bg-white border-slate-300 text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={resetHolidayForm} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-100 transition-all">Ακύρωση</button>
                  <button onClick={saveHoliday} className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all">Αποθήκευση</button>
                </div>
              </div>
            )}
            
            {holidays.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Sun className="w-6 h-6 text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">Δεν έχετε αποθηκευμένες τοπικές αργίες</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
                {holidays.map(holiday => (
                  <div key={holiday.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-all">
                    <div>
                      <p className="font-medium text-slate-900">{holiday.name}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(holiday.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {holiday.location && ` • ${holiday.location}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEditHoliday(holiday)} className="p-1.5 hover:bg-slate-100 rounded">
                        <Edit2 className="w-4 h-4 text-slate-500" />
                      </button>
                      <button onClick={() => deleteHoliday(holiday.id)} className="p-1.5 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4 text-slate-500 hover:text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Appearance Tab */}
        {activeTab === 'appearance' && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Εμφάνιση</h3>
              <p className="text-sm text-slate-500">Προσαρμόστε την εμφάνιση της εφαρμογής</p>
            </div>
            <div className="p-4 space-y-6">
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Θέμα</Label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setAppearanceForm({ ...appearanceForm, theme: 'light' })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      appearanceForm.theme === 'light'
                        ? 'border-red-700 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Sun className="w-5 h-5" />
                    <span>Φωτεινό</span>
                  </button>
                  <button
                    onClick={() => setAppearanceForm({ ...appearanceForm, theme: 'dark' })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      appearanceForm.theme === 'dark'
                        ? 'border-red-700 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Moon className="w-5 h-5" />
                    <span>Σκοτεινό</span>
                  </button>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  * Το σκοτεινό θέμα θα είναι διαθέσιμο σύντομα
                </p>
              </div>
              
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Μέγεθος γραμματοσειράς</Label>
                <div className="flex gap-3">
                  {[
                    { value: 'small', label: 'Μικρό', size: 'text-sm' },
                    { value: 'medium', label: 'Κανονικό', size: 'text-base' },
                    { value: 'large', label: 'Μεγάλο', size: 'text-lg' },
                  ].map(fs => (
                    <button
                      key={fs.value}
                      onClick={() => setAppearanceForm({ ...appearanceForm, font_size: fs.value })}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${fs.size} ${
                        appearanceForm.font_size === fs.value
                          ? 'border-red-700 bg-red-50'
                          : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {fs.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-amber-600 mt-2">
                * Η αλλαγή μεγέθους θα είναι διαθέσιμη σύντομα
              </p>
            </div>
            
            <button onClick={saveAppearance} disabled={saving} className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white text-sm font-medium rounded-md transition-all disabled:bg-slate-300 flex items-center gap-2">
              <Save className="w-4 h-4" />
              Αποθήκευση
            </button>
            </div>
          </div>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
              <h3 className="font-semibold text-slate-900">Εξαγωγή δεδομένων</h3>
              <p className="text-sm text-slate-500">Κατεβάστε τα δεδομένα σας σε μορφή CSV</p>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                <div>
                  <p className="font-medium text-slate-900">Εγγραφές & Προθεσμίες</p>
                  <p className="text-sm text-slate-500">Όλες οι εγγραφές με πελάτες και προθεσμίες</p>
                </div>
                <button onClick={exportEntries} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-100 transition-all flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Λήψη CSV
                </button>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                <div>
                  <p className="font-medium text-slate-900">Οικονομικά στοιχεία</p>
                  <p className="text-sm text-slate-500">Αμοιβές, έξοδα και πληρωμές</p>
                </div>
                <button onClick={exportFinancial} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-100 transition-all flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Λήψη CSV
                </button>
              </div>
            </div>
            
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-sm text-red-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Τα αρχεία CSV μπορούν να ανοιχθούν με Excel, Google Sheets ή άλλο πρόγραμμα υπολογιστικών φύλλων.
              </p>
            </div>
          </div>
        )}

        {/* Cleanup Section */}
        {activeTab === 'cleanup' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-slate-900">Εκκαθάριση δεδομένων</h3>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
              <p className="text-sm text-red-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Η εκκαθάριση θα διαγράψει <strong>οριστικά</strong> όλους τους πελάτες, υποθέσεις, εγγραφές και οικονομικά στοιχεία. Η ενέργεια δεν αναιρείται.
              </p>
              <button
                onClick={async () => {
                  if (window.confirm('Είστε σίγουροι; Θα διαγραφούν ΟΛΟΙ οι πελάτες, υποθέσεις, εγγραφές και οικονομικά στοιχεία.')) {
                    try {
                      const res = await axios.delete(`${API}/admin/clear-all-data`);
                      const d = res.data.deleted;
                      toast.success(`Εκκαθάριση: ${d.clients || 0} πελάτες, ${d.cases || 0} υποθέσεις, ${d.entries || 0} εγγραφές, ${d.financial_entries || 0} οικονομικά`);
                      if (onDataChanged) onDataChanged();
                    } catch (err) {
                      toast.error('Σφάλμα κατά την εκκαθάριση');
                    }
                  }
                }}
                className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-all flex items-center gap-2"
                data-testid="clear-all-data-btn"
              >
                <Trash2 className="w-4 h-4" />
                Διαγραφή όλων των δεδομένων
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Mobile: list then content */}
      <div className="sm:hidden">
        {!mobileShowContent ? (
          /* Mobile: Tab list */
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Ρυθμίσεις</h2>
            </div>
            <nav className="divide-y divide-slate-100">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
                >
                  <span className="flex items-center gap-3">
                    <tab.icon className="w-4 h-4 text-slate-500" />
                    {tab.label}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              ))}
            </nav>
          </div>
        ) : (
          /* Mobile: Content with back button */
          <div>
            <button
              onClick={() => setMobileShowContent(false)}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-3 font-medium"
            >
              <ChevronLeft className="w-4 h-4" />
              Πίσω
            </button>
            <div className="min-w-0">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-900">Στοιχεία χρήστη</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <Label className="text-sm text-slate-600">Όνομα</Label>
                      <Input
                        value={profileForm.profile_name}
                        onChange={(e) => setProfileForm({ ...profileForm, profile_name: toUpperCaseNoAccents(e.target.value) })}
                        placeholder="π.χ. ΙΩΑΝΝΗΣ"
                        className="mt-1 h-9 bg-white border-slate-300 uppercase text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Επώνυμο</Label>
                      <Input
                        value={profileForm.profile_surname}
                        onChange={(e) => setProfileForm({ ...profileForm, profile_surname: toUpperCaseNoAccents(e.target.value) })}
                        placeholder="π.χ. ΠΑΠΑΔΟΠΟΥΛΟΣ"
                        className="mt-1 h-9 bg-white border-slate-300 uppercase text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Email</Label>
                      <Input
                        value={profileForm.profile_email}
                        onChange={(e) => setProfileForm({ ...profileForm, profile_email: e.target.value })}
                        placeholder="email@example.com"
                        className="mt-1 h-9 bg-white border-slate-300 text-sm"
                      />
                    </div>
                    <Button onClick={saveProfile} className="bg-red-700 hover:bg-red-800 text-white text-sm">
                      <Save className="w-4 h-4 mr-2" />
                      Αποθήκευση
                    </Button>
                  </div>
                </div>
              )}
              {/* Cleanup Tab */}
              {activeTab === 'cleanup' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900">Εκκαθάριση δεδομένων</h3>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-4">
                    <p className="text-sm text-red-800">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Η εκκαθάριση θα διαγράψει <strong>οριστικά</strong> όλα τα δεδομένα.
                    </p>
                    <button
                      onClick={async () => {
                        if (window.confirm('Είστε σίγουροι; Θα διαγραφούν ΟΛΟΙ οι πελάτες, υποθέσεις, εγγραφές.')) {
                          try {
                            const res = await axios.delete(`${API}/admin/clear-all-data`);
                            const d = res.data.deleted;
                            toast.success(`Εκκαθάριση: ${d.clients || 0} πελάτες, ${d.cases || 0} υποθέσεις, ${d.entries || 0} εγγραφές`);
                            if (onDataChanged) onDataChanged();
                          } catch (err) {
                            toast.error('Σφάλμα κατά την εκκαθάριση');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Διαγραφή όλων
                    </button>
                  </div>
                </div>
              )}
              {/* Subscription Tab (mobile) */}
              {activeTab === 'subscription' && <SubscriptionTab />}
              {/* Other tabs - show placeholder for now */}
              {!['profile', 'cleanup', 'subscription'].includes(activeTab) && (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
                  <p className="text-sm text-slate-500">Χρησιμοποιήστε desktop για πλήρη πρόσβαση στις ρυθμίσεις "{tabs.find(t => t.id === activeTab)?.label}".</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
